<script lang="ts" setup>
import { useIntervalFn } from '@vueuse/core';
import type { PropType } from 'vue';
import { computed, ref, watch } from 'vue';
import { VTooltip, VIcon } from 'vuetify/components';

import BigNumber from '@exchange/helpers/bignumber';
import { useFundingRate } from '@exchange/libs/funding-rate/service/src';
import type { FundingRateData } from '@exchange/libs/funding-rate/service/src/lib/funding-rate-model.js';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import type { CurrencyModel } from '@exchange/libs/utils/currency/src';

const props = defineProps({
  currencies: { type: Object as PropType<Dictionary<CurrencyModel>>, required: false },
  defaultFiatCurrency: { type: String, required: true },
  market: { type: Object as PropType<MarketModel>, required: true },
});

const fiatVolume = computed(() => {
  const currencyFiatValue = props.currencies?.[CONSTANTS.DEFAULT_FOR_CURRENCY_FORMATTING]?.defaultFiatValue;

  if (currencyFiatValue === undefined) {
    return Number.NaN;
  }

  return new BigNumber(props.market.allDay.globalVolume).times(currencyFiatValue);
});

const quoteCode = computed(() => props.market.quote.code);
const marketPrecision = computed(() => props.market.marketPrecision);
const dayHigh = computed(() => props.market.allDay.high);
const dayLow = computed(() => props.market.allDay.low);
const lastPrice = computed(() => props.market.lastPrice);

const zeroPrecision = CONSTANTS.PRECISION.ZERO;

const fundingRateData = ref<FundingRateData | null>(null);
const { fundingRate, isLoading: isFundingRateLoading } = useFundingRate(computed(() => props.market?.id || ''));

watch(
  [fundingRate, () => props.market?.type],
  ([newVal, marketType]) => {
    if (marketType === MarketType.PERP && newVal) {
      fundingRateData.value = newVal;
    }
  },
  { immediate: true },
);

const getNextFundingTime = () => {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  const currentHour = now.getUTCHours();
  const nextInterval = Math.ceil((currentHour + 1) / 4) * 4;
  const next = new Date(now);
  next.setUTCHours(nextInterval, 0, 0, 0);
  if (next <= now) {
    next.setTime(next.getTime() + 4 * 60 * 60 * 1000);
  }
  return next;
};

const nextFundingTime = ref(getNextFundingTime());
const countdown = ref('');

const updateCountdown = () => {
  const now = new Date();
  const diff = nextFundingTime.value.getTime() - now.getTime();

  if (diff <= 0) {
    nextFundingTime.value = getNextFundingTime();
    updateCountdown();
    return;
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  countdown.value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

useIntervalFn(updateCountdown, 1000);

const isFutures = computed(() => props.market?.type === MarketType.PERP);
</script>

<template>
  <div class="market-header-details-mobile">
    <div class="market-header-details-mobile__item">
      <span class="market-header-details-mobile__label">{{ $t('modules.marketDetails.currentPrice') }}</span>
      <ot-number-display
        class="market-header-details-mobile__value"
        :grouping="true"
        :left-aligned="true"
        :number="lastPrice"
        :precision="marketPrecision"
        :smaller-suffix="true"
        :suffix="quoteCode"
      />
    </div>
    <template v-if="!isFutures">
      <div class="market-header-details-mobile__item">
        <span class="market-header-details-mobile__label">{{ $t('modules.marketDetails.24hHigh') }}</span>
        <ot-number-display
          class="market-header-details-mobile__value"
          :grouping="true"
          :left-aligned="true"
          :number="dayHigh"
          :precision="marketPrecision"
          :smaller-suffix="true"
          :suffix="quoteCode"
        />
      </div>
      <div class="market-header-details-mobile__item">
        <span class="market-header-details-mobile__label">{{ $t('modules.marketDetails.24hLow') }}</span>
        <ot-number-display
          class="market-header-details-mobile__value"
          :grouping="true"
          :left-aligned="true"
          :number="dayLow"
          :precision="marketPrecision"
          :smaller-suffix="true"
          :suffix="quoteCode"
        />
      </div>
    </template>
    <div class="market-header-details-mobile__item">
      <div class="market-header-details-mobile__label-group">
        <span class="market-header-details-mobile__label">{{ $t('modules.marketDetails.24hVolume') }}</span>
        <v-tooltip
          :text="$t('modules.marketDetails.globalVolumeTooltip')"
          location="bottom"
        >
          <template #activator="{ props }">
            <ot-icon
              v-bind="props"
              class="market-header-details-mobile__info-icon"
              name="info-circle"
            />
          </template>
        </v-tooltip>
      </div>
      <ot-number-display
        class="market-header-details-mobile__value"
        :grouping="true"
        :left-aligned="true"
        :number="fiatVolume"
        :precision="zeroPrecision"
        :smaller-suffix="true"
        :suffix="defaultFiatCurrency"
      />
    </div>
    <div
      v-if="isFutures"
      class="market-header-details-mobile__item"
    >
      <span class="market-header-details-mobile__label">Funding / Countdown</span>
      <template v-if="!isFundingRateLoading && fundingRateData && countdown">
        <div class="market-header-details-mobile__value-group">
          <ot-number-display
            class="market-header-details-mobile__value"
            :add-plus-sign="false"
            :colored-percentage="true"
            :left-aligned="true"
            :number="Number(fundingRateData.funding_rate) * 100"
            :percentage="true"
            :precision="4"
          />
          <span class="market-header-details-mobile__countdown">{{ countdown }}</span>
        </div>
      </template>
      <div
        v-else
        class="market-header-details-mobile__value"
      >
        {{ $t('fundamentals.emptyValues.default') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.market-header-details-mobile {
  display: flex;
  padding: 4px 2px;
  border-radius: 16px;
  background-color: rgb(var(--v-theme-elevation-1));

  &__item {
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  &__label {
    color: rgb(var(--v-theme-text-secondary));
    font-size: var(--font-size-3xs);
  }

  &__label-group {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__info-icon {
    width: 16px;
    height: 16px;
    vertical-align: text-bottom;
  }

  &__value-group {
    display: flex;
    align-items: center;
    font-size: var(--font-size-sm);
    gap: 8px;
  }

  &__value {
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-sm);
  }

  &__countdown {
    color: rgb(var(--v-theme-text-error));
  }
}
</style>
